import { Close, Download } from "@mui/icons-material";
import {
  Alert,
  Button,
  ButtonGroup,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  Paper,
  Grid,
} from "@mui/material";
import { useEffect, useState } from "react";
import typography from "../../cssUtils/typography";
import ButtonOne from "../../common/normal-button";
import ButtonThree from "../../common/third-button";
import CustomTabs from "../../common/custom-tabs";

const SAMPLE_CLIENTS_CSV = [
  "SRNO,CLIENT_CODE,FAMILY_CODE,NAME,PHONE_NO,DATE_OF_BIRTH",
  "1,C001,F001,John Doe,919876543210,15-05-1985",
  "2,C002,,Jane Smith,919823456789,",
].join("\n");

const downloadSampleClientsCSV = () => {
  const blob = new Blob([SAMPLE_CLIENTS_CSV], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "sample_clients.csv";
  a.click();
  URL.revokeObjectURL(url);
};

const FileUpload = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [alertMessage, setAlertMessage] = useState("");
  const [uploadStatus, setUploadStatus] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [activeButton, setActiveButton] = useState("Daily");
  const [loading, setLoading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("client");

  useEffect(() => {
    let timeoutId;

    if (alertOpen) {
      timeoutId = setTimeout(() => {
        setAlertOpen(false);
      }, 5000);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [alertOpen]);

  // Keep activeButton in sync with uploadCategory so CustomTabs never
  // receives a value that isn't in its current tabs array.
  useEffect(() => {
    setActiveButton(uploadCategory === "client" ? "Daily" : "Large & MidCap");
    setFile(null);
    const fileInput = document.getElementById("file-upload");
    if (fileInput) fileInput.value = null;
  }, [uploadCategory]);

  const handleButtonClick = (buttonName) => setActiveButton(buttonName);

  const handleFileChange = (event) => setFile(event.target.files[0]);

  const handleUploadClick = async () => {
    const fileInput = document.getElementById("file-upload");
    const URL =
      process.env.NODE_ENV === "production"
        ? "api"
        : `http://localhost:8000/api`;

    let fileUploadUrl = `${URL}/history/upload-csv/`;
    const user_data = JSON.parse(localStorage.getItem("user"));
    const tenant = localStorage.getItem("tenant");

    if (fileInput) {
      const inputFile = fileInput.files[0];
      const inputFileType = activeButton.toLowerCase();

      if (
        [
          "monthly portfolio",
          "annual report",
          "large & midcap",
          "growth plus value",
        ].includes(inputFileType)
      ) {
        fileUploadUrl = `${URL}/investments/documents/upload-chunks/`;
      }

      if (inputFile) {
        const formData = new FormData();
        formData.append("file", inputFile);
        formData.append("file_type", inputFileType);
        formData.append("user_id", user_data.id);

        try {
          setLoading(true);
          const response = await fetch(fileUploadUrl, {
            method: "POST",
            headers: { "X-tenant-Id": tenant },
            body: formData,
          });

          if (response.ok) {
            setUploadStatus("success");
            setAlertOpen(true);
            setAlertMessage(
              "File has been successfully loaded and the customer data will be updated.",
            );
            setFile(null);
            fileInput.value = null;
          } else {
            setUploadStatus("error");
            setAlertOpen(true);

            if (response.status === 413) {
              setAlertMessage(
                "The selected file exceeds the size limit. Please choose a smaller file.",
              );
            } else if (response.status === 400) {
              const data = await response.json();
              if (data?.non_field_errors?.length) {
                setAlertMessage(data.non_field_errors[0]);
              } else {
                setAlertMessage("Failed to upload file.");
              }
            } else {
              setAlertMessage("Failed to upload file.");
            }

            setFile(null);
            fileInput.value = null;
          }
        } catch (error) {
          console.error("Error occurred during file upload:", error);
          setUploadStatus("error");
          setAlertOpen(true);
          setAlertMessage("Error occurred during file upload.");
          setFile(null);
          fileInput.value = null;
        } finally {
          setLoading(false);
        }
      } else {
        setAlertOpen(true);
        setAlertMessage("Please select a file to upload.");
      }
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ ...typography.h1 }}>Upload File</Typography>
      </Box>

      {/* Alert */}
      <Collapse in={alertOpen}>
        <Alert
          sx={{ mb: 2 }}
          severity={uploadStatus === "success" ? "success" : "error"}
        >
          {alertMessage}
        </Alert>
      </Collapse>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      {/* Category */}
      <FormControl sx={{ mb: 3 }}>
        <FormLabel sx={{ ...typography.h2 }}>Upload Category</FormLabel>
        <RadioGroup
          row
          value={uploadCategory}
          onChange={(e) => setUploadCategory(e.target.value)}
        >
          <FormControlLabel
            sx={{ ...typography.bodyLarge }}
            value="client"
            control={<Radio />}
            label="Client Files"
          />
          <FormControlLabel
            sx={{ ...typography.bodyLarge }}
            value="chatbot"
            control={<Radio />}
            label="ChatBot Files"
          />
        </RadioGroup>
      </FormControl>

      {/* Buttons */}
     <Box sx={{ mb: 3 }}>
        <CustomTabs
            value={activeButton}
            onChange={(event, newValue) => handleButtonClick(newValue)}
            tabs={(uploadCategory === "client"
            ? ["Daily", "Monthly", "Clients", "Monthly Portfolio", "Annual Report"]
            : ["Large & MidCap", "Growth Plus Value"]
            ).map((name) => ({ label: name, value: name }))}
        />
        </Box>

      {/* Sample CSV download */}
      {uploadCategory === "client" && activeButton === "Clients" && (
        <Box sx={{ mb: 3 }}>
          <ButtonThree
            startIcon={<Download />}
            onClick={downloadSampleClientsCSV}
          >
            Download Sample CSV
          </ButtonThree>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Required: CLIENT_CODE, NAME, PHONE_NO &nbsp;·&nbsp; Optional: FAMILY_CODE, DATE_OF_BIRTH (format DD-MM-YYYY, column required but value may be left blank per row)
          </Typography>
        </Box>
      )}

      {/* File Input */}
      <Box sx={{ mb: 3 }}>
        <Typography gutterBottom sx={{ ...typography.bodyLarge }}>
          Please select a file to upload:
        </Typography>
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
            backgroundColor: "action.hover",
          }}
        >
          <input type="file" onChange={handleFileChange} id="file-upload" />
        </Box>
      </Box>

      {/* Action */}
      <ButtonOne
        variant="contained"
        onClick={handleUploadClick}
        disabled={loading || !file}
        title={loading ? "Uploading..." : "Upload"}
      />
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Typography sx={{ ...typography.h2, mb: 2 }}>Upload Guidelines</Typography>
            <Typography sx={{ ...typography.bodyLarge, mb: 1 }}>
              • Select the appropriate upload category.
            </Typography>
            <Typography sx={{ ...typography.bodyLarge, mb: 1 }}>
              • Choose the correct file type before uploading.
            </Typography>
            <Typography sx={{ ...typography.bodyLarge }}>
              • Ensure the selected file matches the required format.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FileUpload;